import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Inject,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DateRange, getUTCEndOfDay } from '@vendasta/galaxy/utility/date-utils';
import { ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { DatePickerSelection } from './datepicker-selection';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { getLocale } from '@vendasta/galaxy/utility/locale';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { GalaxyDateAdapter, MY_DATE_FORMATS } from '../date-adapter';

@Component({
  selector: 'glxy-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: getLocale() },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
  ],
})
export class DatepickerComponent implements OnChanges, OnInit, OnDestroy {
  @HostBinding('class') class = 'glxy-datepicker';

  /* Config option for choosing to select a single date or a date range from the datepicker. */
  @Input() selection: DatePickerSelection = DatePickerSelection.single;

  /* A single date to set the datepicker to. The "selection" input needs to be set to single as well. */
  @Input() date?: Date;

  /* A date range (start and end date) to set the datepicker to. The "selection" input needs to be set to range as well. */
  @Input() dateRange?: DateRange;

  /* Allows for selection of dates in the future. Otherwise, users can only select up to the current date. */
  @Input() futureDates?: boolean;

  /* An override for the label to be displayed when no date is selected. */
  @Input() label?: string;

  /*
   * When the "selection" is single and a date is selected that value is emitted.
   * When the "selection" is range and a start AND end date is selected that pair of values is emitted.
   */
  @Output() dateSelected = new EventEmitter<DateRange | Date>();

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  maxDate: Date | undefined = new Date();

  constructor(private _adapter: DateAdapter<GalaxyDateAdapter>, @Inject(MAT_DATE_LOCALE) private _locale: string) {}

  start = new UntypedFormControl();
  end = new UntypedFormControl();
  range = new UntypedFormGroup({
    start: this.start,
    end: this.end,
  });

  ngOnInit(): void {
    if (this.date && this.selection === DatePickerSelection.single) {
      this.start.setValue(new Date(this.date.getUTCFullYear(), this.date.getUTCMonth(), this.date.getUTCDate()));
    }

    if (this.dateRange && this.dateRange.start && this.dateRange.end && this.selection === DatePickerSelection.range) {
      this.start.setValue(
        new Date(
          this.dateRange.start.getUTCFullYear(),
          this.dateRange.start.getUTCMonth(),
          this.dateRange.start.getUTCDate(),
        ),
      );
      this.end.setValue(
        new Date(
          this.dateRange.end.getUTCFullYear(),
          this.dateRange.end.getUTCMonth(),
          this.dateRange.end.getUTCDate(),
        ),
      );
    }

    this.range.valueChanges
      .pipe(
        debounceTime(200),
        filter(() => this.range.valid),
        distinctUntilChanged(
          (a: DateRange, b: DateRange) =>
            a.start?.valueOf() === b.start?.valueOf() && a.end?.valueOf() === b.end?.valueOf(),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe((value) => {
        let dateSelected: Date | DateRange | undefined = value?.start
          ? new Date(Date.UTC(value.start.getFullYear(), value.start.getMonth(), value.start.getDate()))
          : undefined;
        if (this.selection === DatePickerSelection.range) {
          const end = value?.end
            ? getUTCEndOfDay(new Date(Date.UTC(value.end.getFullYear(), value.end.getMonth(), value.end.getDate())))
            : undefined;
          dateSelected = {
            start: dateSelected,
            end: end,
          };
        }
        this.dateSelected.emit(dateSelected);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // handle the "futureDate" input changing
    if (changes.futureDates && changes.futureDates.currentValue) {
      if (changes.futureDates.currentValue) {
        this.maxDate = undefined;
      } else {
        this.maxDate = new Date();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
